/**
 * Request Store Actions
 *
 * Contains all action implementations for the request store.
 * This includes the core business logic for request state transitions.
 *
 * @module @features/requests/stores/request/requestActions
 */

import { RequestModel } from '../../models';
import {
    checkAllConflicts,
    hasBlockingConflicts,
    separateConflictsBySeverity,
    transitionToCancelled,
    transitionToDelivered,
    resubmitFromDeclined,
    updateOrAddRequest,
} from '../../utils';
import type {
    Conflict,
    CreateRequestDto,
    Request,
    UpdateRequestDto,
} from '@features/requests/types';
import type { RequestState, RequestStoreActions } from './requestTypes';

/**
 * Initial state for the request store.
 */
export const initialState: RequestState = {
    requests: [],
    selectedRequestId: null,
    isLoading: false,
    error: null,
};

/**
 * Creates the request actions object.
 *
 * @param set - Zustand set function
 * @param get - Zustand get function
 * @returns RequestStoreActions object
 */
export function createRequestActions(
    set: (
        partial:
            | Partial<RequestState>
            | ((state: RequestState) => Partial<RequestState>),
    ) => void,
    get: () => RequestState & RequestStoreActions,
): RequestStoreActions {
    return {
        createRequest: (dto: CreateRequestDto, employeeId: string): Request => {
            if (!employeeId?.trim()) {
                throw new Error('Ugyldig ansatt-ID');
            }

            const newRequest = RequestModel.create(dto, employeeId);

            set((state: RequestState): Partial<RequestState> => ({
                ...state,
                requests: [...state.requests, newRequest],
                error: null,
            }));

            return newRequest;
        },

        updateRequest: (
            id: string,
            dto: UpdateRequestDto,
            employeeId: string,
        ): Request | null => {
            const state = get();
            const requestIndex = state.requests.findIndex(
                (r: Request): boolean =>
                    r.id === id && r.employeeId === employeeId,
            );

            if (requestIndex === -1) {
                get().setError('Søknad ikke funnet');
                return null;
            }

            const existingRequest = state.requests[requestIndex];

            if (!RequestModel.canEdit(existingRequest)) {
                get().setError(
                    'Kun søknader i Utkast- eller Avslått-status kan redigeres.',
                );
                return null;
            }

            const updatedRequest: Request = {
                ...existingRequest,
                startDate: dto.startDate ?? existingRequest.startDate,
                endDate: dto.endDate ?? existingRequest.endDate,
                notes: dto.notes ?? existingRequest.notes,
                attachments: dto.attachments ?? existingRequest.attachments,
                shiftPreference:
                    dto.shiftPreference ?? existingRequest.shiftPreference,
                excludedDates:
                    dto.excludedDates ?? existingRequest.excludedDates,
                updatedAt: new Date(),
            };

            if (!updatedRequest.startDate || !updatedRequest.endDate) {
                get().setError(
                    'Startdato og sluttdato er påkrevd for oppdatering.',
                );
                return null;
            }

            // Dates are guaranteed non-null by the check above
            const validation = RequestModel.validate({
                ...updatedRequest,
                startDate: updatedRequest.startDate as Date,
                endDate: updatedRequest.endDate as Date,
                type: updatedRequest.type,
            });

            if (!validation.valid) {
                get().setError(validation.error ?? 'Ugyldig oppdatering');
                return null;
            }

            const timingValidation = RequestModel.validateRequestTiming(
                updatedRequest,
                {
                    validateDeadline: false,
                    validateExcludedPeriods: false,
                },
            );
            if (!timingValidation.valid) {
                get().setError(timingValidation.error ?? 'Ugyldig tidsramme');
                return null;
            }

            set((state: RequestState): Partial<RequestState> => ({
                ...state,
                requests: [
                    ...state.requests.slice(0, requestIndex),
                    updatedRequest,
                    ...state.requests.slice(requestIndex + 1),
                ],
                error: null,
            }));

            return updatedRequest;
        },

        submitRequest: async (
            id: string,
            employeeId: string,
        ): Promise<
            | { success: true; request: Request; conflicts?: Conflict[] }
            | { success: false; error: string; conflicts?: Conflict[] }
        > => {
            const state = get();

            let requestIndex = state.requests.findIndex(
                (r: Request): boolean =>
                    r.id === id && r.employeeId === employeeId,
            );

            if (requestIndex === -1) {
                requestIndex = state.requests.findIndex(
                    (r: Request): boolean => r.id === id,
                );
            }

            if (requestIndex === -1) {
                return { success: false, error: 'Søknad ikke funnet' };
            }

            const existingRequest = state.requests[requestIndex];

            const isResubmission = RequestModel.canResubmit(existingRequest);
            if (!RequestModel.canSubmit(existingRequest) && !isResubmission) {
                return {
                    success: false,
                    error: 'Kun søknader i Utkast- eller Avslått-status kan sendes inn.',
                };
            }

            const timingValidation =
                RequestModel.validateRequestTiming(existingRequest);
            if (!timingValidation.valid) {
                return {
                    success: false,
                    error:
                        timingValidation.error ??
                        'Ugyldig tidsramme for innsending.',
                };
            }

            if (!existingRequest.startDate || !existingRequest.endDate) {
                return {
                    success: false,
                    error: 'Startdato og sluttdato er påkrevd for innsending.',
                };
            }

            const conflicts = checkAllConflicts(
                employeeId,
                {
                    startDate: existingRequest.startDate,
                    endDate: existingRequest.endDate,
                    type: existingRequest.type,
                    shiftPreference: existingRequest.shiftPreference,
                },
                state.requests,
            );

            const { errors, warnings } = separateConflictsBySeverity(conflicts);

            const submittedRequest = isResubmission
                ? resubmitFromDeclined(existingRequest, employeeId)
                : transitionToDelivered(existingRequest, employeeId);

            set((state: RequestState): Partial<RequestState> => ({
                ...state,
                requests: [
                    ...state.requests.slice(0, requestIndex),
                    submittedRequest,
                    ...state.requests.slice(requestIndex + 1),
                ],
                error: null,
            }));

            return {
                success: true,
                request: submittedRequest,
                conflicts: hasBlockingConflicts(conflicts)
                    ? errors
                    : warnings.length > 0
                      ? warnings
                      : undefined,
            };
        },

        /**
         * Cancels a DELIVERED request (transitions to CANCELLED). Local only.
         */
        cancelRequest: (id: string, employeeId: string): Request | null => {
            const state = get();
            const requestIndex = state.requests.findIndex(
                (r: Request): boolean =>
                    r.id === id && r.employeeId === employeeId,
            );

            if (requestIndex === -1) {
                get().setError('Søknad ikke funnet');
                return null;
            }

            const existingRequest = state.requests[requestIndex];

            if (!RequestModel.canCancel(existingRequest)) {
                get().setError(
                    'Kun søknader i Innsend-status kan kanselleres.',
                );
                return null;
            }

            const cancelledRequest = transitionToCancelled(
                existingRequest,
                employeeId,
            );

            set((state: RequestState): Partial<RequestState> => ({
                ...state,
                requests: [
                    ...state.requests.slice(0, requestIndex),
                    cancelledRequest,
                    ...state.requests.slice(requestIndex + 1),
                ],
                error: null,
            }));

            return cancelledRequest;
        },

        selectRequest: (id: string): void => {
            set({ selectedRequestId: id, error: null });
        },

        clearSelection: (): void => {
            set({ selectedRequestId: null });
        },

        setError: (error: string): void => {
            set({ error });
        },

        clearError: (): void => {
            set({ error: null });
        },

        setRequests: (requests: Request[]): void => {
            set({ requests, error: null });
        },

        upsertRequest: (request: Request): void => {
            set((state: RequestState): Partial<RequestState> => ({
                ...state,
                requests: updateOrAddRequest(state.requests, request),
                error: null,
            }));
        },

        setLoading: (isLoading: boolean): void => {
            set({ isLoading });
        },

        getRequestById: (id: string): Request | null => {
            const state = get();
            return (
                state.requests.find((r: Request): boolean => r.id === id) ??
                null
            );
        },

        getRequestsByStatus: (status: string): Request[] => {
            const state = get();
            return state.requests.filter(
                (r: Request): boolean => r.status === status,
            );
        },

        getRequestsByType: (type: string): Request[] => {
            const state = get();
            return state.requests.filter(
                (r: Request): boolean => r.type === type,
            );
        },

        resetStore: (): void => {
            set(initialState);
        },
    };
}
